import type { FramingConfig } from "building-types/framing/framingMetaDataTypes";
import type {
  ResolvedOpeningFraming,
  WallFramingInput,
  WallOpeningBoundary,
  WallOpeningFramingConfigGroup,
  WallOpeningFramingResolver,
} from "building-types/framing/wallFramingTypes";
import { polyLine2AABB2 } from "geom/polyLine2";
import type { ClosedPolyLine2 } from "geom/polyLine2BrandedTypes";

/** Subset of {@link ResolvedOpeningFraming} used by the skip predicate. */
export type OpeningFramingSkipInput = Pick<
  ResolvedOpeningFraming,
  "framingDisabled" | "numberOfKingStuds" | "numberOfJackStuds"
>;

export type ShouldSkipOpeningFraming = (resolved: OpeningFramingSkipInput) => boolean;

/**
 * When true, the opening is not passed to the wall framing generator: the region perimeter still
 * includes the void (continuous studs/plates) but no headers, kings, jacks, or cripples are laid out.
 */
export const shouldSkipOpeningFraming: ShouldSkipOpeningFraming = (resolved) => {
  if (resolved.framingDisabled) {
    return true;
  }
  // Temporary until opening framing can be disabled via doc settings. Explicit zero king or jack stud
  // counts mean "treat this cut as plain wall framing region."
  return resolved.numberOfKingStuds === 0 || resolved.numberOfJackStuds === 0;
};

/** Rough opening width from an opening boundary polyline (plane UV); sufficient for skip resolution. */
export function roughWidthFromOpeningPolyline(polyline: ClosedPolyLine2): number {
  const aabb = polyLine2AABB2(polyline);
  return aabb.maxX - aabb.minX;
}

function explicitMemberCount(config: FramingConfig | undefined, defaultCount: number = 1): number {
  if (config?.disabled) {
    return 0;
  }
  if (config === undefined) {
    return defaultCount;
  }
  return config.count;
}

/** Map a debug / direct {@link WallOpeningFramingConfigGroup} onto skip-predicate inputs. */
export function openingFramingSkipInputFromConfigGroup(config: WallOpeningFramingConfigGroup): OpeningFramingSkipInput {
  return {
    framingDisabled: config.framingDisabled ?? false,
    numberOfKingStuds: explicitMemberCount(config.kingStudConfig),
    numberOfJackStuds: explicitMemberCount(config.jackStudConfig),
  };
}

export function filterWallOpeningBoundariesForFraming(
  openings: readonly WallOpeningBoundary[],
  resolveOpeningFramingConfig: WallOpeningFramingResolver,
  getResolvedOpeningFraming: (openingId: string) => ResolvedOpeningFraming | undefined,
  shouldSkip: ShouldSkipOpeningFraming = shouldSkipOpeningFraming,
): WallOpeningBoundary[] {
  return openings.filter((opening) => {
    const openingId = opening.openingId;
    if (openingId === undefined) {
      return true;
    }

    const roughWidth = roughWidthFromOpeningPolyline(opening.polyline);
    resolveOpeningFramingConfig({ openingId, roughWidth });

    const resolved = getResolvedOpeningFraming(openingId);
    if (resolved === undefined) {
      return true;
    }

    return !shouldSkip(resolved);
  });
}

/** Apply opening skip rules to a {@link WallFramingInput} before wall framing V2 generation (debug UI). */
export function filterWallFramingInputOpenings(
  input: WallFramingInput,
  shouldSkip: ShouldSkipOpeningFraming = shouldSkipOpeningFraming,
): WallFramingInput {
  const wallDefault = input.openingFramingConfigGroup;
  if (input.openings === undefined) {
    return input;
  }

  const openings = input.openings.filter((opening) => {
    const config = opening.openingFramingConfigGroup ?? wallDefault;
    if (config === undefined) {
      return true;
    }
    return !shouldSkip(openingFramingSkipInputFromConfigGroup(config));
  });

  if (openings.length === input.openings.length) {
    return input;
  }

  return { ...input, openings };
}
